import { DOORS, EDGE, ROWS, towerMultiplier, type TowerState } from '../../engine/games/tower';
import { money } from '../../format';
import { Burst, Controls, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type ActionSpec, type TableProps } from './Frame';

/**
 * Tower as a chip tower on the felt (user, 2026-09-27: the skyscraper didn't
 * look like a casino). Eight rows of three face-down cards; a safe pick
 * turns up a gold chip and your chip stack grows; a trap turns up BUST and
 * the stack topples.
 */
/** A trap: the BUST card turns up, then the stack topples. */
const FLIP_MS = 450;
const TOPPLE_MS = 900;
/** A ★ trap that gives way: BUST shows and holds (the loss), then the
 *  starburst, then it turns into a chip. */
const CREAK_HOLD_MS = 900;
const CREAK_BANNER_MS = 1200;

type CardLook = 'down' | 'current' | 'dim' | 'chip' | 'bust' | 'hit' | 'creak' | 'blank';

/** Chip colours up the stack, like a real rack: red, green, black, purple... */
const CHIP_COLORS = ['#c0392b', '#2e8b57', '#1c1a17', '#6a3a9a', '#d68a1a', '#2f5fd0', '#c0392b', '#e0b85e'];

function Card({ look, onClick, label }: { look: CardLook; onClick?: () => void; label: string }) {
  const face =
    look === 'chip' ? (
      <span className="tw-chip" aria-hidden="true" />
    ) : look === 'bust' || look === 'hit' || look === 'creak' ? (
      <span className="tw-bust" aria-hidden="true">BUST</span>
    ) : null;
  if (onClick) {
    return (
      <button className={`tw-card is-${look}`} onClick={onClick} aria-label={label}>
        {face}
      </button>
    );
  }
  return (
    <span className={`tw-card is-${look}`} role="img" aria-label={label}>
      {face}
    </span>
  );
}

export function TowerTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = run.tables.tower?.state as TowerState | undefined;
  const anim = snapshot.anim;
  const frames = anim?.frames ?? [];
  const creak = frames.some((f) => f.includes('gives way'));
  const falling = !creak && frames.some((f) => f.includes('Falling'));
  const phase = state?.phase ?? 'idle';
  const total = creak ? FLIP_MS + CREAK_HOLD_MS + CREAK_BANNER_MS : falling ? FLIP_MS + TOPPLE_MS : 300;
  const elapsed = useAnimClock(snapshot, actions, total);
  const [burst, fading, setBurst] = useBurst();
  useLuckMoment(snapshot, elapsed, creak ? FLIP_MS + CREAK_HOLD_MS : null, () => ({ what: 'The trap holds: it turns into a chip.', saved: true }), setBurst);

  const mode = state?.mode ?? 'easy';
  const edge = state && phase !== 'idle' ? state.edge : EDGE;
  const picks = state?.picks ?? [];
  const cleared = picks.length;
  const bet = state && phase !== 'idle' ? state.bet : (run.tables.tower?.bet ?? 0);
  const pay = (rows: number) => Math.round(bet * towerMultiplier(mode, rows, edge));
  const creakRow = creak ? cleared - 1 : -1;
  const creakDone = elapsed >= FLIP_MS + CREAK_HOLD_MS + CREAK_BANNER_MS * 0.4;
  const fellRow = state?.fell ?? null;
  // The stack topples once the BUST card has turned up.
  const toppled = phase === 'done' && fellRow !== null && (!falling || elapsed >= FLIP_MS);

  const pick = (card: number) => {
    if (!state || state.phase !== 'climbing' || anim) return;
    const steps = (card - state.cursor + DOORS) % DOORS;
    for (let k = 0; k < steps; k++) actions.move('down');
    actions.tap();
  };

  const look = (row: number, card: number): CardLook => {
    if (!state || phase === 'idle') return row === 0 ? 'current' : 'down';
    if (row === creakRow && picks[row] === card) return creakDone ? 'chip' : 'creak';
    if (row < cleared) return picks[row] === card ? 'chip' : 'dim';
    if (phase === 'climbing') return row === cleared ? 'current' : 'down';
    // Done: the traps turn up (once a trap's own card has).
    if (fellRow === row && state.cursor === card) return 'hit';
    if ((!falling || elapsed >= FLIP_MS) && state.traps[row]?.includes(card)) return 'bust';
    return 'blank';
  };

  // The stack: one chip per row cleared (a ★ save's chip once it turns up).
  const chips = Math.max(0, cleared - (creak && !creakDone ? 1 : 0));

  const extra: ActionSpec[] = [];
  if (phase === 'climbing' && !anim) {
    extra.push({ id: 'cash', label: cleared === 0 ? 'Take bet back' : `Cash out ${money(pay(cleared))}`, primary: true, onClick: actions.back });
  }

  let result: string | undefined;
  let tone: 'win' | 'loss' | '' = '';
  if (phase === 'done' && state && !anim) {
    const net = state.returned - state.bet;
    result = state.fell !== null ? `Bust on row ${state.fell + 1} · −${money(state.bet)}` : net > 0 ? `${state.dud ? '★ ' : ''}Cashed out on row ${cleared}: +${money(net)}` : 'Took your bet back.';
    tone = state.fell !== null ? 'loss' : net > 0 ? 'win' : '';
  }

  const setMode = (m: 'easy' | 'hard') => {
    if (!state || phase === 'climbing' || m === mode) return;
    actions.tableOption('mode');
  };

  return (
    <>
      <Stage className="tower-stage" onClick={anim ? actions.skip : undefined}>
        <Burst burst={burst} fading={fading} pending={false} />
        {phase !== 'climbing' ? (
          <div className="tower-mode" role="radiogroup" aria-label="Traps per row">
            {(['easy', 'hard'] as const).map((m) => (
              <button key={m} role="radio" aria-checked={mode === m} className={`pick${mode === m ? ' on' : ''}`} onClick={() => setMode(m)} disabled={!!anim}>
                {m === 'easy' ? 'Easy · 1 bust a row' : 'Hard · 2 bust a row'}
              </button>
            ))}
          </div>
        ) : (
          <div className="tower-info">
            <span>Row {cleared + 1} of {ROWS}</span>
            <span>Next: {towerMultiplier(mode, cleared + 1, edge).toFixed(2)}× ({money(pay(cleared + 1))})</span>
          </div>
        )}
        <div className="tw-board">
          <div className={`tw-stack${toppled ? ' toppled' : ''}`} aria-label={`${chips} chips stacked`}>
            {Array.from({ length: chips }, (_, k) => (
              <span key={k} className="tw-stack-chip" style={{ '--c': CHIP_COLORS[k % CHIP_COLORS.length], '--k': k } as React.CSSProperties} />
            ))}
            <span className="tw-stack-base" />
          </div>
          <div className="tw-rows">
            {Array.from({ length: ROWS }, (_, k) => ROWS - 1 - k).map((row) => {
              const here = phase === 'climbing' && row === cleared && !anim;
              return (
                <div key={row} className={`tw-row${here ? ' here' : ''}${row < cleared ? ' done' : ''}`}>
                  <div className="tw-cards">
                    {Array.from({ length: DOORS }, (_, card) => (
                      <Card key={card} look={look(row, card)} onClick={here ? () => pick(card) : undefined} label={`Row ${row + 1}, card ${card + 1}`} />
                    ))}
                  </div>
                  <span className={`tw-plaque${row === cleared - 1 && phase !== 'idle' ? ' now' : row < cleared ? ' won' : ''}`}>
                    {towerMultiplier(mode, row + 1, edge).toFixed(row + 1 >= 6 ? 0 : 1)}×
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <StatusLine snapshot={snapshot} result={result} tone={tone} />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} hide={phase === 'climbing' ? ['mode', 'start'] : ['mode']} extra={extra} onAct={() => setBurst(null)} />
    </>
  );
}
